import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AuditLog } from '../../components/AuditLog'
import { BotaoAcao } from '../../components/BotaoAcao'
import { EscolhaDeIdioma } from '../../components/EscolhaDeIdioma'
import { InboxManager } from '../../components/InboxManager'
import { useSession } from '../../lib/auth-client'
import { avatarColor, initials } from '../../lib/format'
import { t, tRico } from '../../lib/i18n'
import { useWorkspaceRole } from '../../lib/role'
import { useTRPC } from '../../lib/trpc'

export const Route = createFileRoute('/_app/configuracoes')({ component: Settings })

/**
 * Rótulo do papel. Função e não constante: um mapa traduzido no topo do
 * arquivo seria resolvido na importação e ficaria preso no idioma de abertura.
 * A chave continua `agent` no banco e na API; só o rótulo muda.
 */
function rotuloDoPapel(papel: string): string {
  const rotulos: Record<string, string> = {
    owner: t('Dono'),
    admin: t('Administrador'),
    agent: t('Atendente'),
  }
  return rotulos[papel] ?? papel
}

function Settings() {
  const { isAdmin } = useWorkspaceRole()

  return (
    <div className="h-full overflow-y-auto">
      <div className="fim-seguro mx-auto max-w-3xl px-5 pt-4 md:pt-8">
        <h1 className="font-display text-2xl">{t('Configurações')}</h1>

        <SettingsCard
          title={t('Equipe')}
          description={tRico(
            'Quem é {atendente} enxerga apenas os inboxes de que é membro. Dono e administrador enxergam todos.',
            { atendente: <strong>{t('atendente')}</strong> },
          )}
        >
          <MembersSection />
        </SettingsCard>

        {isAdmin && (
          <SettingsCard
            title={t('Convites por link')}
            description={t(
              'Quem abrir o link entra ou cria uma conta e é adicionado ao workspace. O link vale por 7 dias e pode ser revogado a qualquer momento.',
            )}
          >
            <InviteLinks />
          </SettingsCard>
        )}

        {/* Todo o gerenciador de inbox é `adminProcedure`, inclusive a
            listagem — para o atendente não sobra nem o que olhar. */}
        {isAdmin && (
          <SettingsCard
            title={t('Inboxes')}
            description={tRico(
              'Cada inbox tem funil, fila e equipe próprios. Um {atendente} só enxerga conversas dos inboxes de que é membro; dono e administrador enxergam todos.',
              { atendente: <strong>{t('atendente')}</strong> },
            )}
          >
            <InboxManager />
          </SettingsCard>
        )}

        {/* Depois de Inboxes, como pedido. Só admin: a trilha diz quem fez o
            quê, e num workspace com atendentes isso é informação de quem
            administra, não de quem atende. O cartão não usa padding próprio —
            a lista sangra até a borda, que é o que deixa a paginação
            encostada no rodapé do cartão. */}
        {isAdmin && (
          <SettingsCard
            title={t('Logs')}
            description={t(
              'O que foi feito no workspace: contas, inboxes, convites, membros e apagamentos. Do mais recente para o mais antigo.',
            )}
            semRecuo
          >
            <AuditLog />
          </SettingsCard>
        )}

        {/* Por último, e fora de cartão de propósito: é a única linha desta
            página que não configura o workspace, e sim quem está olhando para
            ela. Sem `isAdmin` — a escolha é de cada um, inclusive do
            atendente, que não enxerga nenhum dos cartões acima. */}
        <EscolhaDeIdioma />
      </div>
    </div>
  )
}

/**
 * Cada assunto num cartão com cabeçalho próprio. Antes as seções eram títulos
 * soltos empilhados e a página lia como uma lista corrida — o cartão marca
 * onde um assunto termina e o outro começa.
 */
function SettingsCard({
  title,
  description,
  children,
  semRecuo = false,
}: {
  title: string
  description?: React.ReactNode
  children: React.ReactNode
  /** Conteúdo que sangra até a borda — lista com divisórias e rodapé próprio. */
  semRecuo?: boolean
}) {
  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-line bg-surface">
      <header className="border-b border-line bg-surface-2/50 px-4 py-3">
        <h2 className="text-md font-semibold text-ink">{title}</h2>
        {description && <p className="mt-0.5 text-xs leading-relaxed text-ink-2">{description}</p>}
      </header>
      <div className={semRecuo ? '' : 'p-4'}>{children}</div>
    </section>
  )
}

function MembersSection() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const members = useQuery(trpc.workspace.members.queryOptions())
  const session = useSession()
  const meuId = session.data?.user?.id
  const { isAdmin, isOwner } = useWorkspaceRole()
  const [confirmando, setConfirmando] = useState<string | null>(null)
  /** Qual membro está com o painel de inboxes aberto. */
  const [inboxesDe, setInboxesDe] = useState<string | null>(null)

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: [['workspace']] })
    queryClient.invalidateQueries({ queryKey: [['inbox']] })
  }

  const setRole = useMutation({
    ...trpc.workspace.setMemberRole.mutationOptions(),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    ...trpc.workspace.removeMember.mutationOptions(),
    onSuccess: () => {
      setConfirmando(null)
      invalidate()
    },
  })

  return (
    <>
      {(setRole.error || remove.error) && (
        <p role="alert" className="mb-3 rounded-lg bg-danger-soft px-3 py-2 text-2xs text-danger">
          {t(setRole.error?.message ?? remove.error?.message ?? '')}
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {(members.data ?? []).map((member) => (
          <li
            key={member.userId}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2.5"
          >
            <span
              className="grid size-8 flex-none place-items-center rounded-full text-2xs font-bold text-white"
              style={{ background: avatarColor(member.userId) }}
            >
              {initials(member.name)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold">{member.name}</span>
              <span className="block truncate text-2xs text-ink-3">{member.email}</span>
            </span>

            {/* Sobre si mesmo não há o que oferecer — o servidor recusa os dois
                casos; e quem não é admin não gerencia ninguém. Em ambos, um
                controle que sempre dá erro é pior que controle nenhum. */}
            {member.userId === meuId || !isAdmin ? (
              <span className="flex-none rounded-full bg-surface-2 px-2 py-0.5 text-2xs font-semibold text-ink-2">
                {rotuloDoPapel(member.role)}
                {member.userId === meuId && ` · ${t('você')}`}
              </span>
            ) : (
              <>
                <select
                  value={member.role}
                  onChange={(event) =>
                    setRole.mutate({
                      userId: member.userId,
                      role: event.target.value as 'owner' | 'admin' | 'agent',
                    })
                  }
                  aria-label={t('Papel de {nome}', { nome: member.name })}
                  className="flex-none rounded-md border border-line bg-surface px-2 py-1 text-2xs"
                >
                  {['owner', 'admin', 'agent'].map((value) => (
                    // Só o dono concede ou remove o papel de dono — para um
                    // admin a opção existe, mas não é selecionável.
                    <option key={value} value={value} disabled={value === 'owner' && !isOwner}>
                      {rotuloDoPapel(value)}
                    </option>
                  ))}
                </select>

                {/* Só para atendente: dono e admin enxergam todos os
                    inboxes por definição, e oferecer marcação a eles daria a
                    entender que dá para restringir — não dá. */}
                {member.role === 'agent' && (
                  <BotaoAcao
                    aria-expanded={inboxesDe === member.userId}
                    onClick={() => setInboxesDe(inboxesDe === member.userId ? null : member.userId)}
                  >
                    {t('Inboxes')}
                  </BotaoAcao>
                )}

                <BotaoAcao
                  variante="destrutiva"
                  onClick={() =>
                    confirmando === member.userId
                      ? remove.mutate({ userId: member.userId })
                      : setConfirmando(member.userId)
                  }
                  onBlur={() => setConfirmando(null)}
                  className={confirmando === member.userId ? 'border-danger/40 text-danger' : ''}
                >
                  {confirmando === member.userId ? t('Confirmar remoção') : t('Remover')}
                </BotaoAcao>
              </>
            )}

            {inboxesDe === member.userId && (
              <InboxesDoMembro userId={member.userId} nome={member.name} onChange={invalidate} />
            )}
          </li>
        ))}
      </ul>

      {isAdmin && (
        <p className="mt-2 text-2xs leading-relaxed text-ink-3">
          {t(
            'Ao remover alguém, as conversas atribuídas a essa pessoa ficam sem responsável — não são apagadas. Você não consegue alterar o próprio papel nem remover a si mesmo.',
          )}
        </p>
      )}
    </>
  )
}

/** Papéis que um convite pode conceder — dono não se concede por link. */
const ROLE_OPTIONS = ['agent', 'admin'] as const

/**
 * Convite por link, não por e-mail: o produto não depende de provedor de
 * envio, e quem convida compartilha pelo canal que já usa com a equipe.
 */
function InviteLinks() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const invites = useQuery(trpc.invite.list.queryOptions())
  const inboxes = useQuery(trpc.inbox.list.queryOptions())

  const [label, setLabel] = useState('')
  const [role, setRole] = useState<'agent' | 'admin'>('agent')
  const [inboxIds, setInboxIds] = useState<string[]>([])
  const [unlimited, setUnlimited] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: [['invite']] })
  }

  const create = useMutation({
    ...trpc.invite.create.mutationOptions(),
    onSuccess: () => {
      setLabel('')
      setInboxIds([])
      invalidate()
    },
  })
  const revoke = useMutation({ ...trpc.invite.revoke.mutationOptions(), onSuccess: invalidate })

  function linkFor(token: string) {
    return `${window.location.origin}/convite/${token}`
  }

  /**
   * O que dizer sobre um convite.
   *
   * "Esgotado" vinha antes de tudo e era a pior informação possível: o link
   * tinha funcionado, alguém tinha entrado, e a tela anunciava um limite. Quem
   * aceitou passa na frente — é a resposta para a pergunta que se faz olhando
   * um convite usado.
   */
  function statusOf(item: {
    revokedAt: Date | string | null
    expiresAt: Date | string
    maxUses: number | null
    usedCount: number
    aceitoPor?: Array<{ nome: string | null }>
  }) {
    const aceites = item.aceitoPor ?? []
    if (aceites.length > 0) {
      const nomes = aceites.map((a) => a.nome).filter(Boolean)
      const texto =
        nomes.length === 1
          ? t('Aceito por {nome}', { nome: nomes[0]! })
          : nomes.length > 1
            ? t('Aceito por {n} pessoas', { n: nomes.length })
            : t('Aceito')
      return { text: texto, active: false, aceito: true }
    }

    if (item.revokedAt) return { text: t('Revogado'), active: false, aceito: false }
    if (new Date(item.expiresAt).getTime() < Date.now()) {
      return { text: t('Expirado'), active: false, aceito: false }
    }
    if (item.maxUses !== null && item.usedCount >= item.maxUses) {
      return { text: t('Esgotado'), active: false, aceito: false }
    }
    const restante =
      item.maxUses === null ? t('ilimitado') : t('{n} uso(s)', { n: item.maxUses - item.usedCount })
    return { text: restante, active: true, aceito: false }
  }

  return (
    <div>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          create.mutate({
            label: label || undefined,
            role,
            inboxIds,
            maxUses: unlimited ? null : 1,
          })
        }}
        className="rounded-lg border border-line bg-surface-2/40 p-3"
      >
        <div className="grid gap-2 sm:grid-cols-2">
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder={t('Para quem é? (opcional)')}
            className="rounded-md border border-line-strong bg-surface px-2 py-1.5 text-xs"
          />
          <select
            value={role}
            onChange={(event) => setRole(event.target.value as 'agent' | 'admin')}
            className="rounded-md border border-line-strong bg-surface px-2 py-1.5 text-xs"
          >
            {ROLE_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {rotuloDoPapel(value)}
              </option>
            ))}
          </select>
        </div>

        {role === 'agent' && (
          <div className="mt-3">
            <p className="mb-1.5 text-2xs text-ink-3">
              {t(
                'Inboxes que o atendente vai enxergar — sem nenhum marcado, ele entra sem acesso a conversas.',
              )}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(inboxes.data ?? []).map((row) => {
                const checked = inboxIds.includes(row.id)
                return (
                  <button
                    key={row.id}
                    type="button"
                    aria-pressed={checked}
                    onClick={() =>
                      setInboxIds((current) =>
                        checked ? current.filter((id) => id !== row.id) : [...current, row.id],
                      )
                    }
                    className="rounded-full border border-line px-2.5 py-1 text-2xs font-semibold text-ink-2 hover:border-line-strong aria-[pressed=true]:border-ink aria-[pressed=true]:bg-ink aria-[pressed=true]:text-paper"
                  >
                    {row.name}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        <label className="mt-3 flex items-center gap-2 text-2xs text-ink-2">
          <input
            type="checkbox"
            checked={unlimited}
            onChange={(event) => setUnlimited(event.target.checked)}
          />
          {t('Permitir vários usos (link de equipe)')}
        </label>

        {create.error && (
          <p role="alert" className="mt-2 text-2xs text-danger">
            {t(create.error.message)}
          </p>
        )}

        <button
          type="submit"
          disabled={create.isPending}
          className="mt-3 rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-on-accent disabled:opacity-50"
        >
          {create.isPending ? t('Gerando…') : t('Gerar link')}
        </button>
      </form>

      <ul className="mt-3 flex flex-col gap-2">
        {(invites.data ?? []).map((item) => {
          const status = statusOf(item)
          return (
            <li key={item.id} className="rounded-lg border border-line bg-surface p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold">{item.label ?? t('Sem rótulo')}</span>
                <span className="rounded-full bg-surface-2 px-2 py-0.5 text-2xs text-ink-2">
                  {rotuloDoPapel(item.role)}
                </span>
                {/* Aceito ganha a mesma cor de "ativo": o link cumpriu o que
                    tinha para cumprir, e pintar isso de cinza de esgotado era
                    o que fazia um sucesso parecer com um limite. */}
                <span
                  className="rounded-full px-2 py-0.5 text-2xs"
                  style={
                    status.active || status.aceito
                      ? { color: 'var(--st-resolv)', background: 'var(--st-resolv-soft)' }
                      : { color: 'var(--ink-3)', background: 'var(--surface-2)' }
                  }
                  title={
                    status.aceito
                      ? item.aceitoPor
                          ?.map((a) => a.nome)
                          .filter(Boolean)
                          .join(', ')
                      : undefined
                  }
                >
                  {status.text}
                </span>
                {status.active && (
                  <span className="ml-auto">
                    <BotaoAcao
                      variante="destrutiva"
                      onClick={() => revoke.mutate({ inviteId: item.id })}
                    >
                      {t('Revogar')}
                    </BotaoAcao>
                  </span>
                )}
              </div>

              {status.active && (
                <div className="mt-2 flex items-center gap-2">
                  <code className="min-w-0 flex-1 truncate rounded bg-surface-2 px-2 py-1 text-2xs text-ink-2">
                    {linkFor(item.token)}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(linkFor(item.token))
                      setCopiedId(item.id)
                      setTimeout(() => setCopiedId(null), 1200)
                    }}
                    className="shrink-0 rounded border border-line px-2 py-1 text-2xs text-ink-2 hover:bg-surface-2"
                  >
                    {copiedId === item.id ? t('Copiado') : t('Copiar')}
                  </button>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * Quais inboxes um atendente enxerga, editável depois do aceite.
 *
 * Existe porque só havia o caminho inverso — abrir cada inbox e mexer na
 * equipe. Quem precisa ajustar uma pessoa tinha de percorrer todos os inboxes
 * procurando onde ela está, e quem entrou por convite ficava para sempre com o
 * que foi marcado na criação do link.
 *
 * As mesmas procedures dos dois lados (`inbox.addMember` / `removeMember`):
 * são a mesma operação vista de outro ângulo, e duplicar a regra criaria duas
 * autorizações para manter em sincronia.
 */
function InboxesDoMembro({
  userId,
  nome,
  onChange,
}: {
  userId: string
  nome: string
  onChange: () => void
}) {
  const trpc = useTRPC()
  const inboxes = useQuery(trpc.inbox.listAll.queryOptions())
  const doMembro = useQuery(trpc.inbox.membershipsOf.queryOptions({ userId }))

  const adicionar = useMutation({ ...trpc.inbox.addMember.mutationOptions(), onSuccess: onChange })
  const tirar = useMutation({ ...trpc.inbox.removeMember.mutationOptions(), onSuccess: onChange })

  const atuais = new Set(doMembro.data ?? [])
  const ocupado = adicionar.isPending || tirar.isPending

  return (
    <div className="mt-2 w-full rounded-lg border border-line bg-surface-2 p-3">
      <p className="mb-2 text-2xs text-ink-3">
        {tRico('Conversas que {nome} enxerga. Sem nenhum marcado, não enxerga nada.', {
          nome: <strong className="text-ink-2">{nome}</strong>,
        })}
      </p>

      {doMembro.isPending ? (
        <p className="text-2xs text-ink-3">{t('Carregando…')}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {(inboxes.data ?? []).map((row) => {
            const tem = atuais.has(row.id)
            return (
              <label
                key={row.id}
                className={`flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-2xs font-semibold ${
                  tem
                    ? 'border-accent-soft-line bg-accent-soft text-accent-ink'
                    : 'border-line bg-surface text-ink-2'
                } ${ocupado ? 'opacity-60' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={tem}
                  disabled={ocupado}
                  onChange={(event) =>
                    event.target.checked
                      ? adicionar.mutate({ inboxId: row.id, userId, role: 'agent' })
                      : tirar.mutate({ inboxId: row.id, userId })
                  }
                  className="size-3 accent-[var(--accent-ink)]"
                />
                {row.name}
              </label>
            )
          })}
        </div>
      )}

      {(adicionar.error || tirar.error) && (
        <p role="alert" className="mt-2 text-2xs text-danger">
          {t(adicionar.error?.message ?? tirar.error?.message ?? '')}
        </p>
      )}
    </div>
  )
}
